import { useState, useEffect, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle, XCircle, Loader2, Home, Calendar, Gift, UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { API, useAuth } from "@/App";
import { authHeaders } from "@/lib/firebase";
import { MetaPixel } from "@/lib/metapixel";
import BrandMark from "@/components/BrandMark";

const PaymentSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [status, setStatus] = useState("checking"); // checking, success, failed
  const [message, setMessage] = useState("Verifying your payment...");
  const [reservationId, setReservationId] = useState(null);
  // 2-for-1 referral +1 flow
  const [plusOneState, setPlusOneState] = useState(null); // { status, code }
  const [plusOneName, setPlusOneName] = useState("");
  const [plusOneEmail, setPlusOneEmail] = useState("");
  const [plusOneBusy, setPlusOneBusy] = useState(false);
  const [plusOneDone, setPlusOneDone] = useState(null); // { name, email }
  const hasPolled = useRef(false);

  const handleRedeemPlusOne = async () => {
    const sessionId = searchParams.get("session_id");
    if (!sessionId || !plusOneName.trim()) {
      toast.error("Please enter your +1's name");
      return;
    }
    setPlusOneBusy(true);
    try {
      const headers = { ...(await authHeaders()), "Content-Type": "application/json" };
      const resp = await fetch(`${API}/referral/redeem`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          session_id: sessionId,
          plus_one_name: plusOneName.trim(),
          plus_one_email: plusOneEmail.trim() || null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        toast.error(data.detail || "Failed to add your +1");
        return;
      }
      toast.success(`Added ${data.plus_one_name} to the event 🎉`);
      setPlusOneDone({ name: data.plus_one_name, email: data.plus_one_email });
      setPlusOneState(null);
    } catch (e) {
      toast.error("Network error");
    } finally {
      setPlusOneBusy(false);
    }
  };

  useEffect(() => {
    const sessionId = searchParams.get("session_id");
    
    if (!sessionId) {
      setStatus("failed");
      setMessage("No payment session found");
      return;
    }

    if (hasPolled.current) return;
    hasPolled.current = true;

    const pollPaymentStatus = async (attempts = 0) => {
      const maxAttempts = 10;
      const pollInterval = 2000;

      if (attempts >= maxAttempts) {
        setStatus("failed");
        setMessage("Payment verification timed out. Please check your dashboard or contact support.");
        return;
      }

      try {
        const headers = await authHeaders();
        const response = await fetch(`${API}/payments/status/${sessionId}`, { headers });

        if (!response.ok) {
          throw new Error("Failed to check payment status");
        }

        const data = await response.json();

        if (data.payment_status === "paid") {
          setStatus("success");
          setMessage(data.message || "Payment successful! You're in the game.");
          setReservationId(data.reservation_id);
          // If the buyer entered a valid 2-for-1 code, show the +1 form
          if (data.plus_one_status === "pending") {
            setPlusOneState({ status: "pending", code: data.plus_one_code });
          } else if (data.plus_one_status === "redeemed" && data.plus_one_name) {
            setPlusOneDone({ name: data.plus_one_name });
          } else if (data.plus_one_status === "invalid_code") {
            setPlusOneState({ status: "invalid_code" });
          }
          // Fire Meta Pixel Purchase with the right content_category so Ads
          // Manager can slice conversions by event type (game/series/trip).
          const category = data.content_category || (data.trip_id ? "trip" : data.series_id ? "series" : "game");
          const contentId = data.content_id || data.trip_id || data.series_id || data.game_id || "";
          MetaPixel.purchase(
            category,
            contentId,
            data.content_name || "",
            data.amount || 0,
            data.currency || "GBP"
          );
          toast.success("Payment confirmed! 🎉");
          return;
        } else if (data.status === "expired") {
          setStatus("failed");
          setMessage("Payment session expired. Please try again.");
          return;
        }

        // Continue polling if still pending
        setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
      } catch (error) {
        console.error("Payment status error:", error);
        if (attempts < maxAttempts - 1) {
          setTimeout(() => pollPaymentStatus(attempts + 1), pollInterval);
        } else {
          setStatus("failed");
          setMessage("Error verifying payment. Please check your dashboard.");
        }
      }
    };

    // Start polling after a brief delay to allow Stripe to process
    setTimeout(() => pollPaymentStatus(), 1000);
  }, [searchParams]);

  return (
    <div className="min-h-screen bg-background flex flex-col" data-testid="payment-success-page">
      {/* Header */}
      <header className="p-4 border-b border-border">
        <BrandMark size="md" />
      </header>

      {/* Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <Card className="w-full max-w-md glass-card">
          <CardContent className="flex flex-col items-center justify-center py-12">
            {status === "checking" && (
              <>
                <div className="w-20 h-20 rounded-full bg-primary/20 flex items-center justify-center mb-6">
                  <Loader2 className="w-10 h-10 text-primary animate-spin" />
                </div>
                <h2 className="font-display text-2xl mb-2">VERIFYING PAYMENT</h2>
                <p className="text-muted-foreground text-center">{message}</p>
              </>
            )}

            {status === "success" && (
              <>
                <div className="w-20 h-20 rounded-full bg-emerald-500/20 flex items-center justify-center mb-6">
                  <CheckCircle className="w-10 h-10 text-emerald-500" />
                </div>
                <h2 className="font-display text-2xl mb-2 text-emerald-500">PAYMENT CONFIRMED</h2>
                <p className="text-muted-foreground text-center mb-6">{message}</p>

                {/* 2-for-1 +1 collection form */}
                {plusOneState?.status === "pending" && !plusOneDone && (
                  <div
                    className="w-full mb-6 rounded-lg border border-primary/40 bg-primary/10 p-4"
                    data-testid="plus-one-form"
                  >
                    <div className="flex items-center gap-2 mb-2">
                      <Gift className="w-4 h-4 text-primary-ink" />
                      <h3 className="text-sm font-semibold text-foreground">
                        You used code {plusOneState.code} — bring a friend for free!
                      </h3>
                    </div>
                    <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
                      Tell us who&apos;s coming with you. They&apos;ll be added to the event at no extra charge.
                    </p>
                    <div className="space-y-2.5">
                      <div>
                        <Label htmlFor="plus-one-name" className="text-xs">Their name *</Label>
                        <Input
                          id="plus-one-name"
                          value={plusOneName}
                          onChange={(e) => setPlusOneName(e.target.value)}
                          placeholder="e.g. Sam Wilson"
                          disabled={plusOneBusy}
                          autoFocus
                          data-testid="plus-one-name-input"
                        />
                      </div>
                      <div>
                        <Label htmlFor="plus-one-email" className="text-xs">Their email (optional)</Label>
                        <Input
                          id="plus-one-email"
                          type="email"
                          value={plusOneEmail}
                          onChange={(e) => setPlusOneEmail(e.target.value)}
                          placeholder="sam@example.com"
                          disabled={plusOneBusy}
                          data-testid="plus-one-email-input"
                        />
                      </div>
                      <Button
                        onClick={handleRedeemPlusOne}
                        disabled={plusOneBusy || !plusOneName.trim()}
                        className="w-full mt-1"
                        data-testid="plus-one-submit-btn"
                      >
                        {plusOneBusy ? (
                          <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Adding your +1…</>
                        ) : (
                          <><UserPlus className="w-4 h-4 mr-2" /> Add my +1</>
                        )}
                      </Button>
                    </div>
                  </div>
                )}

                {plusOneState?.status === "invalid_code" && (
                  <div className="w-full mb-6 rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-muted-foreground" data-testid="plus-one-invalid">
                    We couldn&apos;t apply the 2-for-1 code you entered — it may have expired, already been used for this event, or belonged to someone who hasn&apos;t attended a paid Pouls event yet. Your booking is confirmed either way.
                  </div>
                )}

                {plusOneDone && (
                  <div className="w-full mb-6 rounded-lg border border-emerald-500/40 bg-emerald-500/10 p-3 flex items-center gap-2" data-testid="plus-one-confirmed">
                    <CheckCircle className="w-4 h-4 text-emerald-500 shrink-0" />
                    <p className="text-xs text-foreground">
                      <span className="font-medium">{plusOneDone.name}</span> is coming with you.
                    </p>
                  </div>
                )}

                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <Button 
                    onClick={() => navigate("/profile")}
                    className="flex-1 btn-glow bg-primary text-primary-foreground hover:bg-primary/90 font-display"
                    data-testid="view-reservations-btn"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    My Profile
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/")}
                    className="flex-1 border-border"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Home
                  </Button>
                </div>
              </>
            )}

            {status === "failed" && (
              <>
                <div className="w-20 h-20 rounded-full bg-destructive/20 flex items-center justify-center mb-6">
                  <XCircle className="w-10 h-10 text-destructive" />
                </div>
                <h2 className="font-display text-2xl mb-2 text-destructive">PAYMENT ISSUE</h2>
                <p className="text-muted-foreground text-center mb-6">{message}</p>
                
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <Button 
                    onClick={() => navigate("/")}
                    className="flex-1 bg-primary text-primary-foreground hover:bg-primary/90 font-display"
                  >
                    <Home className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => navigate("/profile")}
                    className="flex-1 border-border"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Profile
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default PaymentSuccessPage;
